import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  detectPlatform,
  detectRegion,
  getAppStoreUrl,
  isMobileDevice,
  type Platform,
  type Region,
} from '../../utils/mobileAppBanner';
import { CloseIcon } from '../Icons/CloseIcon';

export const MobileAppBanner = () => {
  const { t } = useTranslation();
  const [platform, setPlatform] = useState<Platform>('unknown');
  const [region, setRegion] = useState<Region>('global');
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const testMobile = urlParams.get('testMobile');

    const userAgent = navigator.userAgent;
    let platformToUse: Platform = 'unknown';

    if (testMobile === 'ios' || testMobile === 'android') {
      platformToUse = testMobile;
    } else if (isMobileDevice(userAgent)) {
      platformToUse = detectPlatform(userAgent);
    } else {
      return;
    }

    const detectedRegion = detectRegion();
    setPlatform(platformToUse);
    setRegion(detectedRegion);
    setIsVisible(true);
  }, []);

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (platform === 'unknown') {
      return;
    }

    const appStoreUrl = getAppStoreUrl(platform, region);
    window.open(appStoreUrl, '_blank');
  };

  const handleClose = () => {
    setIsVisible(false);
    localStorage.setItem('mobile_app_banner_dismissed', Date.now().toString());
  };

  useEffect(() => {
    const dismissed = localStorage.getItem('mobile_app_banner_dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed, 10);
      const dayInMs = 24 * 60 * 60 * 1000;
      if (Date.now() - dismissedTime < dayInMs) {
        setIsVisible(false);
      }
    }
  }, []);

  if (!isVisible || platform === 'unknown') {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[100000]">
      <div className="w-full flex flex-col items-center rounded-t-[16px] px-5 pt-5 pb-4 gap-[23px] bg-[rgba(64,64,64,0.80)] backdrop-blur-[12px] relative">
        <button
          onClick={handleClose}
          className="absolute top-2 -right-0 p-1 text-white/60 hover:text-white transition-colors z-10 bg-[rgba(64,64,64,0.80)] backdrop-blur-[12px] rounded-full">
          <CloseIcon width={20} height={20} fillColor="#FFFFFF" />
        </button>
        <div className="self-stretch text-center text-white text-xl font-semibold font-['DM_Sans'] leading-7 tracking-[-0.2px]">
          {t('mobileBanner.getFullExperience')}
        </div>
        <div className="self-stretch flex justify-center items-center">
          <button
            onClick={handleOpen}
            className="flex justify-center items-center cursor-pointer rounded-full w-[156px] h-10 py-1 px-4"
            style={{
              background:
                'radial-gradient(114.48% 83.24% at 83.65% 100%, rgba(253, 229, 213, 0.50) 0%, rgba(253, 229, 213, 0.00) 100%), linear-gradient(180deg, rgba(255, 255, 255, 0.50) 0%, rgba(0, 0, 0, 0.50) 100%), #BE369D',
              backgroundBlendMode: 'normal, overlay, normal',
            }}>
            <div className="text-white text-xs font-['DM_Sans'] leading-4">
              {t('mobileBanner.open')}
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};
